import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { FileText, Upload, Download, AlertCircle, FolderUp, ShieldCheck, Sparkles } from "lucide-react";
import AnalysisCard from "./AnalysisCard";

const MAX_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 25;

// Allowed: PDFs, common image types, Word/Excel-ish docs (uploaded as raw)
const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt";

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function getResourceType(file) {
  return file.type.startsWith("image/") ? "image" : "raw";
}

function DocumentRow({ doc, onDownload, downloading }) {
  const [expanded, setExpanded] = useState(!!doc.has_analysis);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const pollTimer = useRef(null);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}/analysis`);
      setAnalysis(data.analysis || null);
      return data.analysis || null;
    } catch {
      setAnalysis(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // When the doc has an existing analysis, fetch it once. Re-runs when doc.id or has_analysis flips.
  useEffect(() => {
    if (doc.has_analysis && !analysis) {
      fetchAnalysis().then((a) => {
        // If a job is still in flight (e.g., user reloaded the page mid-analysis), resume polling
        if (a && a.status === "running") {
          setRunning(true);
          pollUntilDone();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, doc.has_analysis]);

  // Cleanup any pending poll timer on unmount
  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const pollUntilDone = (attempts = 0) => {
    const MAX = 200;           // ~10 minutes at 3s interval
    const INTERVAL_MS = 3000;
    pollTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/documents/${doc.id}/analysis`);
        const a = data.analysis;
        if (a && a.status === "running") {
          if (attempts < MAX) {
            pollUntilDone(attempts + 1);
            return;
          }
          // Still "running" after 10 minutes — surface a retryable timeout instead of spinning forever
          setAnalysis({
            status: "failed",
            result: { error: "analysis_failed", message: "This analysis is taking much longer than expected. Click Try Again to re-run it." },
          });
          setRunning(false);
          return;
        }
        // done, failed, or legacy record without status
        setAnalysis(a || null);
        setRunning(false);
      } catch {
        if (attempts < MAX) {
          pollUntilDone(attempts + 1);
        } else {
          setAnalysis({ result: { error: "request_failed", message: "Couldn't reach the analysis service. Please try again." } });
          setRunning(false);
        }
      }
    }, INTERVAL_MS);
  };

  const runAnalysis = async () => {
    setRunning(true);
    setExpanded(true);
    if (pollTimer.current) clearTimeout(pollTimer.current);
    try {
      const { data } = await api.post(`/documents/${doc.id}/analyze`);
      setAnalysis(data.analysis || { status: "running" });
      if (data.queued || data.analysis?.status === "running") {
        pollUntilDone();
      } else {
        setRunning(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Analysis failed.";
      setAnalysis({
        result: { error: "request_failed", message: msg },
      });
      setRunning(false);
    }
  };

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !analysis && doc.has_analysis) {
      fetchAnalysis();
    }
  };

  return (
    <li
      className="rounded-lg border border-white/10 hover:border-[var(--if-gold)]/40 transition-all overflow-hidden"
      data-testid={`document-row-${doc.id}`}
    >
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-5 h-5 text-[var(--if-gold)] shrink-0" />
          <div className="min-w-0">
            <div className="text-sm text-white truncate" style={{ fontFamily: "Inter, sans-serif" }} title={doc.filename}>
              {doc.filename}
            </div>
            <div className="text-xs text-white/50" style={{ fontFamily: "Inter, sans-serif" }}>
              {fmtBytes(doc.size_bytes)} · {fmtDate(doc.created_at)}
              {doc.has_analysis && <span className="ml-2 text-[var(--if-gold)]">· analyzed</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {doc.has_analysis ? (
            <button
              onClick={toggleExpand}
              className="btn-outline !py-2 !px-3 !text-[11px]"
              data-testid={`document-toggle-analysis-${doc.id}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {expanded ? "Hide" : "View"}
            </button>
          ) : (
            <button
              onClick={runAnalysis}
              disabled={running}
              className="btn-gold !py-2 !px-3 !text-[11px] disabled:opacity-60"
              data-testid={`document-analyze-${doc.id}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {running ? "Reading…" : "Analyze"}
            </button>
          )}
          <button
            onClick={() => onDownload(doc)}
            disabled={downloading}
            className="btn-outline !py-2 !px-3 !text-[11px]"
            data-testid={`document-download-${doc.id}`}
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? "…" : "Open"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3">
          {loading && !analysis && (
            <div className="text-xs text-white/50 italic p-3" data-testid={`analysis-loading-${doc.id}`}>
              Loading analysis…
            </div>
          )}
          {running && !analysis && (
            <div className="text-xs text-white/70 p-3 rounded-lg border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/[0.04] flex items-center gap-2" data-testid={`analysis-running-${doc.id}`}>
              <Sparkles className="w-3.5 h-3.5 text-[var(--if-gold)] animate-pulse" />
              Sergeant Credit is reading your report — this can take 20-40 seconds…
            </div>
          )}
          {analysis && (
            <AnalysisCard
              document={doc}
              analysis={analysis}
              onRunAnalysis={runAnalysis}
              running={running}
            />
          )}
        </div>
      )}
    </li>
  );
}

export default function DocumentsPanel() {
  const [items, setItems] = useState([]);
  const [used, setUsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/documents/me");
      setItems(data.items || []);
      setUsed(data.used ?? (data.items || []).length);
    } catch (e) {
      console.warn("Documents load failed:", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-pick of same file
    if (!file) return;
    setError("");

    if (file.size > MAX_BYTES) {
      setError(`File too large — max 25 MB. "${file.name}" is ${fmtBytes(file.size)}.`);
      return;
    }
    if (used >= MAX_FILES) {
      setError(`You've reached the ${MAX_FILES}-document limit. Contact your strategist to free up space.`);
      return;
    }

    const resource_type = getResourceType(file);
    setUploading(true);
    setProgress(0);

    try {
      // 1. Get signed params from backend
      const { data: sig } = await api.post("/documents/signature", {
        filename: file.name,
        size_bytes: file.size,
        resource_type,
      });

      // 2. Upload directly to Cloudinary
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      form.append("type", "authenticated");

      const xhr = new XMLHttpRequest();
      const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resource_type}/upload`;

      const uploaded = await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              reject(new Error("Bad upload response"));
            }
          } else {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.open("POST", cloudinaryUrl);
        xhr.send(form);
      });

      // 3. Register with backend
      await api.post("/documents", {
        public_id: uploaded.public_id,
        filename: file.name,
        size_bytes: file.size,
        resource_type,
        file_format: uploaded.format || "",
        secure_url: uploaded.secure_url || "",
      });

      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed.";
      setError(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const { data } = await api.get(`/documents/${doc.id}/download`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError("Couldn't generate a download link. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const remaining = Math.max(0, MAX_FILES - used);

  return (
    <div className="card-dark p-6" data-testid="documents-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-[var(--if-gold)]" />
        <span className="font-mono-label text-[var(--if-gold)]">Secure Documents</span>
      </div>
      <h3 className="font-display text-2xl mb-1">Upload your records.</h3>
      <p className="text-sm text-white/60 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
        Send credit reports, IDs, statements, and supporting docs. Files are encrypted at rest and only your strategist can review them. Max 25 MB per file, up to {MAX_FILES} files.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={onFile}
        className="hidden"
        data-testid="documents-file-input"
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={handlePick}
          disabled={uploading || remaining === 0}
          className="btn-gold !py-2.5 !px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="documents-upload-btn"
        >
          {uploading ? (
            <>
              <Upload className="w-4 h-4 animate-pulse" />
              Uploading {progress}%
            </>
          ) : (
            <>
              <FolderUp className="w-4 h-4" />
              Upload document
            </>
          )}
        </button>
        <span className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }} data-testid="documents-remaining">
          {used}/{MAX_FILES} used · {remaining} slots left
        </span>
      </div>

      {uploading && (
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-4" data-testid="documents-progress">
          <div
            className="h-full bg-[var(--if-gold)] transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {error && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm mb-4"
          data-testid="documents-error"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span style={{ fontFamily: "Inter, sans-serif" }}>{error}</span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-white/45 border border-dashed border-white/10 rounded-lg" style={{ fontFamily: "Inter, sans-serif" }}>
          No documents yet. Once uploaded, files become read-only — contact your strategist to remove them.
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="documents-list">
          {items.map((d) => (
            <DocumentRow
              key={d.id}
              doc={d}
              onDownload={onDownload}
              downloading={downloadingId === d.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
